import React from 'react';
import { i18n } from 'i18n';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Divider
} from '@mui/material';
import { Trans } from '@lingui/react';
import { PipeDataDto } from 'providers/api';

interface PropertyMappingDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (mappings: PropertyMappings) => void;
  availableProperties: string[];
  availableVertexAttributes: string[];
  availablePipeTypes: PipeDataDto[];
  uniquePropertyValues: Record<string, string[]>;
}

export interface PropertyMappings {
  nodeIdAttribute: string;
  pipeTypeProperty: string;
  pipeTypeMappings: Record<string, string>;
}

const PropertyMappingDialog: React.FC<PropertyMappingDialogProps> = ({
  open,
  onClose,
  onConfirm,
  availableProperties,
  availableVertexAttributes,
  availablePipeTypes,
  uniquePropertyValues,
}) => {
  const [nodeIdAttribute, setNodeIdAttribute] = React.useState<string>('');
  const [pipeTypeProperty, setPipeTypeProperty] = React.useState<string>('');
  const [pipeTypeMappings, setPipeTypeMappings] = React.useState<Record<string, string>>({});

  const hasVertexAttributes = availableVertexAttributes.length > 0;

  const handleNodeIdAttributeChange = (value: string) => {
    setNodeIdAttribute(value);
  };

  const handlePipeTypePropertyChange = (value: string) => {
    setPipeTypeProperty(value);
    setPipeTypeMappings({});
  };

  const handlePipeTypeMappingChange = (propertyValue: string, pipeId: string) => {
    setPipeTypeMappings((prev) => ({
      ...prev,
      [propertyValue]: pipeId,
    }));
  };

  const handleConfirm = () => {
    const mappings: PropertyMappings = {
      nodeIdAttribute,
      pipeTypeProperty,
      pipeTypeMappings,
    };
    onConfirm(mappings);
  };

  const canConfirm = true;

  const uniqueValuesForPipeType = pipeTypeProperty ? (uniquePropertyValues[pipeTypeProperty] || []) : [];

  React.useEffect(() => {
    if (nodeIdAttribute && !availableVertexAttributes.includes(nodeIdAttribute)) {
      setNodeIdAttribute('');
    }
  }, [availableVertexAttributes]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '400px' },
      }}
    >
      <DialogTitle>
        <Trans id="Map Properties">Map Properties</Trans>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Node ID Vertex Attribute Selection */}
          <Box>
            <Typography variant="h6" gutterBottom>
              <Trans id="Node ID Mapping">Node ID Mapping</Trans>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {hasVertexAttributes ? (
                <Trans id="Select a vertex attribute to use for node IDs. Vertices without a value get the default naming.">
                  Select a vertex attribute to use for node IDs. Vertices without a value get the default naming.
                </Trans>
              ) : (
                <Trans id="The selected layers have no vertex attributes, so the default naming will be used.">
                  The selected layers have no vertex attributes, so the default naming will be used.
                </Trans>
              )}
            </Typography>
            <FormControl fullWidth disabled={!hasVertexAttributes}>
              <InputLabel>
                <Trans id="Node ID Vertex Attribute">Node ID Vertex Attribute</Trans>
              </InputLabel>
              <Select
                value={nodeIdAttribute}
                label={i18n._('Node ID Vertex Attribute')}
                onChange={(e) => handleNodeIdAttributeChange(e.target.value)}
              >
                <MenuItem value="">
                  <em><Trans id="Use default naming">Use default naming</Trans></em>
                </MenuItem>
                {availableVertexAttributes.map((attribute) => (
                  <MenuItem key={attribute} value={attribute}>
                    {attribute}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Divider />

          {/* Pipe Type Property Selection */}
          <Box>
            <Typography variant="h6" gutterBottom>
              <Trans id="Pipe Type Mapping">Pipe Type Mapping</Trans>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <Trans id="Select a property that contains pipe type information, then map each unique value to your existing pipe types.">
                Select a property that contains pipe type information, then map each unique value to your existing pipe types.
              </Trans>
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>
                <Trans id="Pipe Type Property">Pipe Type Property</Trans>
              </InputLabel>
              <Select
                value={pipeTypeProperty}
                label={i18n._('Pipe Type Property')}
                onChange={(e) => handlePipeTypePropertyChange(e.target.value)}
              >
                <MenuItem value="">
                  <em><Trans id="Use default pipe type">Use default pipe type</Trans></em>
                </MenuItem>
                {availableProperties.map((property) => (
                  <MenuItem key={property} value={property}>
                    {property}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Pipe Type Value Mappings */}
            {pipeTypeProperty && uniqueValuesForPipeType.length > 0 && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  <Trans id="Map pipe type values">Map pipe type values:</Trans>
                </Typography>
                {uniqueValuesForPipeType.map((value) => (
                  <Box key={`pipe-value:${value}`} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Typography variant="body2" sx={{ minWidth: '120px', fontWeight: 'medium' }}>
                      {value}
                      :
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: '200px' }}>
                      <Select
                        value={pipeTypeMappings[value] || ''}
                        onChange={(e) => handlePipeTypeMappingChange(value, e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">
                          <em><Trans id="Select pipe type">Select pipe type</Trans></em>
                        </MenuItem>
                        {availablePipeTypes.map((pipe) => (
                          <MenuItem key={pipe.pipeId} value={pipe.pipeId}>
                            {pipe.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          <Trans id="Cancel">Cancel</Trans>
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!canConfirm}
        >
          <Trans id="Continue">Continue</Trans>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PropertyMappingDialog;
